import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Paper, Chip, Button, Divider, Container, Skeleton } from '@mui/material';
import { ArrowBack as ArrowBackIcon, CalendarToday, Article as ArticleIcon } from '@mui/icons-material';
import { newsService, type NewsItem } from '../services/newsService';

const NewsDetail: React.FC<{ item: NewsItem; onBack: () => void }> = ({ item, onBack }) => (
  <Box>
    <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small" sx={{ mb: 2 }}>
      К новостям
    </Button>
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.cover_color, flexShrink: 0 }} />
        <Chip label={item.status === 'draft' ? 'Черновик' : 'Опубликовано'} size="small"
          color={item.status === 'published' ? 'success' : 'default'} sx={{ fontSize: '0.7rem' }} />
      </Box>
      <Typography variant="h5" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
        {item.title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3 }}>
        <CalendarToday sx={{ fontSize: 14, color: '#999' }} />
        <Typography variant="caption" color="text.secondary">
          {new Date(item.published_at || item.created_at || '').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
        </Typography>
        {item.author_name && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {item.author_name}
          </Typography>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#1e293b' }}>
        {item.content.split('\n').map((line, i) => {
          if (line.startsWith('## ')) return <Typography key={i} variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>{line.slice(3)}</Typography>;
          if (line.startsWith('### ')) return <Typography key={i} variant="subtitle1" fontWeight={700} sx={{ mt: 1.5, mb: 0.5 }}>{line.slice(4)}</Typography>;
          if (line.startsWith('- **')) {
            const match = line.match(/^- \*\*(.+?)\*\*[—–-]?\s*(.*)$/);
            if (match) return <Typography key={i} variant="body2" sx={{ ml: 2, mb: 0.5 }}><strong>{match[1]}</strong>{match[2] ? ` — ${match[2]}` : ''}</Typography>;
          }
          if (line.startsWith('- ')) return <Typography key={i} variant="body2" sx={{ ml: 2, mb: 0.5 }}>• {line.slice(2)}</Typography>;
          if (/^\d+\./.test(line)) return <Typography key={i} variant="body2" sx={{ ml: 2, mb: 0.5 }}>{line}</Typography>;
          if (line.trim() === '') return <Box key={i} sx={{ height: 8 }} />;
          return <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>{line}</Typography>;
        })}
      </Box>
    </Paper>
  </Box>
);

const NewsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailItem, setDetailItem] = useState<NewsItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    newsService.getPublished().then(setNews).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (id) {
      setDetailLoading(true);
      newsService.getPublishedById(Number(id)).then(setDetailItem).finally(() => setDetailLoading(false));
    } else {
      setDetailItem(null);
    }
  }, [id]);

  if (id) {
    if (detailLoading) {
      return (
        <Container maxWidth="md">
          <Box sx={{ py: 3 }}><Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} /></Box>
        </Container>
      );
    }
    if (!detailItem) {
      return (
        <Container maxWidth="md">
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">Новость не найдена</Typography>
            <Button component={RouterLink} to="/news" sx={{ mt: 2 }}>К новостям</Button>
          </Box>
        </Container>
      );
    }
    return (
      <Container maxWidth="md">
        <Box sx={{ py: { xs: 2, sm: 3 } }}>
          <NewsDetail item={detailItem} onBack={() => navigate('/news')} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <ArticleIcon sx={{ color: '#1565c0' }} />
          <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            Новости Nashrelax.ru
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />)}
          </Box>
        ) : news.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Новостей пока нет</Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {news.map((item, idx) => (
              <Paper
                key={item.id}
                component={RouterLink}
                to={`/news/${item.id}`}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  display: 'flex',
                  gap: 2,
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: '#1565c0' },
                }}
              >
                <Box sx={{
                  width: { xs: 36, sm: 44 },
                  height: { xs: 36, sm: 44 },
                  borderRadius: 2,
                  bgcolor: item.cover_color || '#1565c0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: { xs: '0.9rem', sm: '1.1rem' },
                  flexShrink: 0,
                }}>
                  {idx + 1}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, mb: 0.25 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                    fontSize: { xs: '0.78rem', sm: '0.85rem' },
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    {item.excerpt}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                    <CalendarToday sx={{ fontSize: 12, color: '#999' }} />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(item.published_at || item.created_at || '').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default NewsPage;